/**
 * Shared formatters for displaying special field types like notes and structured data.
 */
import React from 'react';

// Note entry structure from the backend
interface NoteEntry {
  source: string;
  timestamp: string;
  text: string;
  vendor?: string;
  user_email?: string;
}

/**
 * Format notes array for display with source attribution and timestamps.
 * Accepts either a JSON string or an already-parsed array.
 */
export function formatNotesForDisplay(value: string | NoteEntry[]): React.ReactNode {
  let parsed: NoteEntry[];

  if (Array.isArray(value)) {
    parsed = value;
  } else {
    try {
      const result = JSON.parse(value);
      if (!Array.isArray(result)) {
        return <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{value}</pre>;
      }
      parsed = result;
    } catch {
      return <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{value}</pre>;
    }
  }

  if (parsed.length === 0) {
    return <span style={{ color: 'var(--color-text-muted)' }}>No notes</span>;
  }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
        {parsed.map((note: NoteEntry, index: number) => {
          const date = new Date(note.timestamp);
          const formattedDate = date.toLocaleString();
          const sourceLabel = note.source === 'vendor'
            ? `Vendor: ${note.vendor || 'Unknown'}`
            : note.source === 'app_user'
              ? `User: ${note.user_email || 'Unknown'}`
              : 'Migration';

          return (
            <div
              key={index}
              style={{
                padding: 'var(--spacing-sm)',
                backgroundColor: note.source === 'vendor'
                  ? 'var(--color-bg-info)'
                  : 'var(--color-bg-tertiary)',
                borderRadius: 'var(--radius-sm)',
                borderLeft: `3px solid ${note.source === 'vendor' ? 'var(--color-primary)' : 'var(--color-border)'}`,
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
                marginBottom: 'var(--spacing-xs)',
              }}>
                <span style={{ fontWeight: 500 }}>{sourceLabel}</span>
                <span>{formattedDate}</span>
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{note.text}</div>
            </div>
          );
        })}
      </div>
    );
}

/**
 * Format structured_data/extra fields for display with sections.
 */
export function formatStructuredDataForDisplay(value: string): React.ReactNode {
  try {
    const parsed = JSON.parse(value);
    if (typeof parsed !== 'object' || parsed === null) {
      return <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{value}</pre>;
    }

    // Separate internal fields from extra data
    const internalFields = ['vendor', 'vendor_case_id', 'ingestion_id'];
    const internal: Record<string, unknown> = {};
    const extra: Record<string, unknown> = {};

    for (const [key, val] of Object.entries(parsed)) {
      if (internalFields.includes(key)) {
        internal[key] = val;
      } else {
        extra[key] = val;
      }
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
        {Object.keys(extra).length > 0 && (
          <div>
            <div style={{
              fontWeight: 600,
              fontSize: 'var(--font-size-sm)',
              marginBottom: 'var(--spacing-xs)',
              color: 'var(--color-text-secondary)',
            }}>
              Extra Fields from Vendor
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gap: 'var(--spacing-xs) var(--spacing-md)',
            }}>
              {Object.entries(extra).map(([key, val]) => (
                <React.Fragment key={key}>
                  <span style={{ fontWeight: 500, color: 'var(--color-text-muted)' }}>
                    {key.replace(/_/g, ' ')}:
                  </span>
                  <span>{typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
        {Object.keys(internal).length > 0 && (
          <div>
            <div style={{
              fontWeight: 600,
              fontSize: 'var(--font-size-sm)',
              marginBottom: 'var(--spacing-xs)',
              color: 'var(--color-text-muted)',
            }}>
              Internal Metadata
            </div>
            <div style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
              fontFamily: 'var(--font-family-mono)',
            }}>
              {Object.entries(internal).map(([key, val]) => (
                <div key={key}>{key}: {String(val)}</div>
              ))}
            </div>
          </div>
        )}
        {Object.keys(extra).length === 0 && Object.keys(internal).length === 0 && (
          <span style={{ color: 'var(--color-text-muted)' }}>No extra fields</span>
        )}
      </div>
    );
  } catch {
    return <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{value}</pre>;
  }
}

/**
 * Check if a value looks like a JSON notes array.
 * Accepts both a JSON string or an already-parsed array.
 */
export function isNotesJson(value: unknown): boolean {
  // Handle already-parsed arrays
  if (Array.isArray(value)) {
    return value.length > 0 && value[0]?.source !== undefined;
  }
  // Handle JSON strings
  if (typeof value !== 'string') return false;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) && parsed.length > 0 && parsed[0]?.source !== undefined;
  } catch {
    return false;
  }
}

/**
 * Check if a value looks like structured data JSON.
 */
export function isStructuredDataJson(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  try {
    const parsed = JSON.parse(value);
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed);
  } catch {
    return false;
  }
}
